/**
 * Where the floating tutorial box sits and how big it is.
 *
 * Pure functions, kept out of the component on purpose: this is the logic that
 * decides whether the box is REACHABLE, and it is worth testing without a DOM.
 * The bug it exists to prevent is not hypothetical — the box used to be placed
 * once on first open and never re-checked, so rotating a phone or shrinking a
 * window left it stranded outside the viewport for the rest of the session,
 * recoverable only by reloading the page.
 */

export interface Geom {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface Viewport {
    w: number;
    h: number;
}

export const MIN_W = 300;
export const MIN_H = 232;
export const DEFAULT_W = 380;
/** Header + a 16:9 video + the controls row, at DEFAULT_W. */
export const DEFAULT_H = 336;
/** Kept clear in the bottom-right for the AI chat button (56px + its 24px
 *  inset, plus breathing room). The box is the higher z-index, so overlapping
 *  the FAB does not just look bad — it swallows its taps. */
export const FAB_RESERVE = 92;
export const EDGE = 8;

export const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/**
 * Force a box fully inside the viewport.
 *
 * Note the `Math.max(EDGE, ...)` on both upper bounds. On a viewport narrower
 * than the box, `vw - w - EDGE` goes negative and a naive clamp would return a
 * negative coordinate, i.e. push the box off the left edge — the exact failure
 * it is supposed to prevent. Pinning to EDGE means a too-small viewport
 * overflows to the RIGHT, where the box is still grabbable by its header.
 */
export function fitToViewport(g: Geom, vp: Viewport): Geom {
    const w = clamp(g.w, MIN_W, Math.max(MIN_W, vp.w - EDGE * 2));
    const h = clamp(g.h, MIN_H, Math.max(MIN_H, vp.h - EDGE * 2));
    return {
        w,
        h,
        x: clamp(g.x, EDGE, Math.max(EDGE, vp.w - w - EDGE)),
        y: clamp(g.y, EDGE, Math.max(EDGE, vp.h - h - EDGE)),
    };
}

/** Bottom-right, but ABOVE the chat button rather than on top of it. */
export function defaultGeom(vp: Viewport): Geom {
    const w = Math.min(DEFAULT_W, vp.w - EDGE * 2);
    const h = Math.min(DEFAULT_H, vp.h - EDGE * 2);
    return fitToViewport({ w, h, x: vp.w - w - 24, y: vp.h - h - FAB_RESERVE }, vp);
}

/** Parse a persisted geometry, rejecting anything that is not four numbers.
 *  Returns null so the caller can fall back to the default placement. */
export function parseGeom(raw: string | null | undefined): Geom | null {
    if (!raw) return null;
    try {
        const p = JSON.parse(raw) as Partial<Geom>;
        const ok = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
        if (ok(p.x) && ok(p.y) && ok(p.w) && ok(p.h)) return { x: p.x, y: p.y, w: p.w, h: p.h };
    } catch {
        // Hand-edited localStorage, or a value from an older shape.
    }
    return null;
}
